/**
 * i18n initialization — react-i18next + browser language detector.
 *
 * Loaded once at app startup via import in src/index.tsx. After that,
 * every component can call `useTranslation(ns?)` to get a `t(key)` fn.
 *
 * Detection priority:
 *   1. localStorage 'i18nextLng' (user choice persists)
 *   2. navigator.language (browser locale)
 *   3. fallback to FALLBACK_LOCALE (en)
 *
 * Translation files are imported statically below — bundled into the
 * main JS chunk. When namespace count grows beyond ~5 + locales > 4,
 * migrate to i18next-http-backend lazy loading. For the current single
 * 'common' namespace, eager loading is the right call (negligible
 * bundle impact, zero round-trips).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  SUPPORTED_LOCALES,
  FALLBACK_LOCALE,
  DEFAULT_NAMESPACE,
  NAMESPACES,
} from './config';

import enCommon from './locales/en/common.json';
import enErrors from './locales/en/errors.json';
import enOrg from './locales/en/org.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enSystem from './locales/en/system.json';
import enProcesses from './locales/en/processes.json';
import esProcesses from './locales/es/processes.json';
import ptProcesses from './locales/pt/processes.json';
import frProcesses from './locales/fr/processes.json';
import deProcesses from './locales/de/processes.json';
import jaProcesses from './locales/ja/processes.json';
import zhCNProcesses from './locales/zh-CN/processes.json';
import arProcesses from './locales/ar/processes.json';
import koProcesses from './locales/ko/processes.json';

import enAttendance from './locales/en/attendance.json';
import esAttendance from './locales/es/attendance.json';
import ptAttendance from './locales/pt/attendance.json';
import frAttendance from './locales/fr/attendance.json';
import deAttendance from './locales/de/attendance.json';
import jaAttendance from './locales/ja/attendance.json';
import zhCNAttendance from './locales/zh-CN/attendance.json';
import arAttendance from './locales/ar/attendance.json';
import koAttendance from './locales/ko/attendance.json';

import enIntegrations from './locales/en/integrations.json';
import ptIntegrations from './locales/pt/integrations.json';
import frIntegrations from './locales/fr/integrations.json';
import deIntegrations from './locales/de/integrations.json';
import jaIntegrations from './locales/ja/integrations.json';
import zhCNIntegrations from './locales/zh-CN/integrations.json';
import arIntegrations from './locales/ar/integrations.json';
import koIntegrations from './locales/ko/integrations.json';

import enWorker from './locales/en/worker.json';
import esWorker from './locales/es/worker.json';
import ptWorker from './locales/pt/worker.json';
import frWorker from './locales/fr/worker.json';
import deWorker from './locales/de/worker.json';
import jaWorker from './locales/ja/worker.json';
import zhCNWorker from './locales/zh-CN/worker.json';
import arWorker from './locales/ar/worker.json';
import koWorker from './locales/ko/worker.json';

import ptOrg from './locales/pt/org.json';
import frOrg from './locales/fr/org.json';
import deOrg from './locales/de/org.json';
import jaOrg from './locales/ja/org.json';
import zhCNOrg from './locales/zh-CN/org.json';
import arOrg from './locales/ar/org.json';
import koOrg from './locales/ko/org.json';

import esCommon from './locales/es/common.json';
import esErrors from './locales/es/errors.json';
import esOrg from './locales/es/org.json';
import esAuth from './locales/es/auth.json';
import esDashboard from './locales/es/dashboard.json';
import esSystem from './locales/es/system.json';
import esIntegrations from './locales/es/integrations.json';

import ptCommon from './locales/pt/common.json';
import ptErrors from './locales/pt/errors.json';
import ptAuth from './locales/pt/auth.json';
import ptDashboard from './locales/pt/dashboard.json';
import ptSystem from './locales/pt/system.json';

import frCommon from './locales/fr/common.json';
import frErrors from './locales/fr/errors.json';
import frAuth from './locales/fr/auth.json';
import frDashboard from './locales/fr/dashboard.json';
import frSystem from './locales/fr/system.json';

import deCommon from './locales/de/common.json';
import deErrors from './locales/de/errors.json';
import deAuth from './locales/de/auth.json';
import deDashboard from './locales/de/dashboard.json';
import deSystem from './locales/de/system.json';

import jaCommon from './locales/ja/common.json';
import jaErrors from './locales/ja/errors.json';
import jaAuth from './locales/ja/auth.json';
import jaDashboard from './locales/ja/dashboard.json';
import jaSystem from './locales/ja/system.json';

import zhCNCommon from './locales/zh-CN/common.json';
import zhCNErrors from './locales/zh-CN/errors.json';
import zhCNAuth from './locales/zh-CN/auth.json';
import zhCNDashboard from './locales/zh-CN/dashboard.json';
import zhCNSystem from './locales/zh-CN/system.json';

import arCommon from './locales/ar/common.json';
import arErrors from './locales/ar/errors.json';
import arAuth from './locales/ar/auth.json';
import arDashboard from './locales/ar/dashboard.json';
import arSystem from './locales/ar/system.json';

import koCommon from './locales/ko/common.json';
import koErrors from './locales/ko/errors.json';
import koAuth from './locales/ko/auth.json';
import koDashboard from './locales/ko/dashboard.json';
import koSystem from './locales/ko/system.json';

const resources = {
  en: { common: enCommon, errors: enErrors, org: enOrg, auth: enAuth, dashboard: enDashboard, system: enSystem, processes: enProcesses, attendance: enAttendance, integrations: enIntegrations, worker: enWorker },
  es: { common: esCommon, errors: esErrors, org: esOrg, auth: esAuth, dashboard: esDashboard, system: esSystem, processes: esProcesses, attendance: esAttendance, integrations: esIntegrations, worker: esWorker },
  pt: { common: ptCommon, errors: ptErrors, org: ptOrg, auth: ptAuth, dashboard: ptDashboard, system: ptSystem, processes: ptProcesses, attendance: ptAttendance, integrations: ptIntegrations, worker: ptWorker },
  fr: { common: frCommon, errors: frErrors, org: frOrg, auth: frAuth, dashboard: frDashboard, system: frSystem, processes: frProcesses, attendance: frAttendance, integrations: frIntegrations, worker: frWorker },
  de: { common: deCommon, errors: deErrors, org: deOrg, auth: deAuth, dashboard: deDashboard, system: deSystem, processes: deProcesses, attendance: deAttendance, integrations: deIntegrations, worker: deWorker },
  ja: { common: jaCommon, errors: jaErrors, org: jaOrg, auth: jaAuth, dashboard: jaDashboard, system: jaSystem, processes: jaProcesses, attendance: jaAttendance, integrations: jaIntegrations, worker: jaWorker },
  'zh-CN': { common: zhCNCommon, errors: zhCNErrors, org: zhCNOrg, auth: zhCNAuth, dashboard: zhCNDashboard, system: zhCNSystem, processes: zhCNProcesses, attendance: zhCNAttendance, integrations: zhCNIntegrations, worker: zhCNWorker },
  ar: { common: arCommon, errors: arErrors, org: arOrg, auth: arAuth, dashboard: arDashboard, system: arSystem, processes: arProcesses, attendance: arAttendance, integrations: arIntegrations, worker: arWorker },
  ko: { common: koCommon, errors: koErrors, org: koOrg, auth: koAuth, dashboard: koDashboard, system: koSystem, processes: koProcesses, attendance: koAttendance, integrations: koIntegrations, worker: koWorker },
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    defaultNS: DEFAULT_NAMESPACE,
    ns: [...NAMESPACES],
    interpolation: {
      // React already escapes; double-escaping breaks glyphs like &.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    react: {
      // Suspense not used — React 19 SSR-friendly, but we render
      // synchronously today.
      useSuspense: false,
    },
    // Dev-only: warn loudly when a key is missing so we don't ship
    // half-translated strings without noticing.
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler:
      process.env.NODE_ENV === 'development'
        ? (lngs, ns, key) => {
            // eslint-disable-next-line no-console
            console.warn(`[i18n] missing key: ${ns}:${key} (lngs=${lngs.join(',')})`);
          }
        : undefined,
  });

// RTL support: set document direction on init + language change
const RTL_SET = new Set(['ar']);
const setDir = (lng: string) => {
  document.documentElement.dir = RTL_SET.has(lng) ? 'rtl' : 'ltr';
  document.documentElement.lang = lng;
};
setDir((i18n as any).language || 'en');
(i18n as any).on('languageChanged', setDir);

export default i18n;
